import { useState } from "react";
import { Link } from "react-router-dom";
import type { EnvironmentDetailResponse } from "@starsach/shared-types";
import { useContactRecords } from "../../api/hooks";
import { SetupChecklist } from "../../components/SetupChecklist";
import { Card, DataTable, EmptyState, ErrorState, LoadingState, RelativeTime, StatusPill, inputClass } from "../../components/ui";
import { contactsChecks, contactsFilterOnly } from "../../lib/setup-checks";
import { useFormat } from "../../lib/useFormat";
import { CallLookup } from "../customer/JourneysTab";
import { OUTCOME_LABEL, OUTCOME_TONE } from "./outcomes";
import { TestPill, TestTrafficFigure } from "./TestTraffic";

const day = (d: Date) => d.toISOString().slice(0, 10);

/**
 * Journeys · Contacts: every contact the environment's stack has sent a
 * record for (ADR-0015) - labels and timings, never content - newest
 * first, filtered by when, channel and how it ended. Staff with the
 * inspector role open a contact into its trace, and can still search the
 * customer's account by number beneath. #448: real contacts by default;
 * the test ones on their own, or everything, from the Traffic filter, each
 * test contact with a Test pill.
 */
export function ContactsSection({ environmentId, canInspect, detail }: { environmentId: string; canInspect: boolean; detail: EnvironmentDetailResponse }) {
  const fmt = useFormat();
  const [from, setFrom] = useState(day(new Date(Date.now() - 7 * 86_400_000)));
  const [to, setTo] = useState(day(new Date(Date.now() + 86_400_000)));
  const [channel, setChannel] = useState("");
  const [outcome, setOutcome] = useState("");
  const [traffic, setTraffic] = useState<"real" | "test" | "all">("real");
  const q = useContactRecords(environmentId, { from, to, channel, outcome, limit: 100, traffic });

  return (
    <div className="grid gap-5">
      <Card title="Contacts">
        <div className="mb-4 flex flex-wrap items-end gap-3">
          <label className="text-sm">
            <span className="block text-xs text-neutral-500">From</span>
            <input type="date" className={inputClass} value={from} onChange={(e) => setFrom(e.target.value)} aria-label="From" />
          </label>
          <label className="text-sm">
            <span className="block text-xs text-neutral-500">To</span>
            <input type="date" className={inputClass} value={to} onChange={(e) => setTo(e.target.value)} aria-label="To" />
          </label>
          <label className="text-sm">
            <span className="block text-xs text-neutral-500">Channel</span>
            <select className={inputClass} value={channel} onChange={(e) => setChannel(e.target.value)} aria-label="Channel">
              <option value="">Any</option>
              <option value="VOICE">Voice</option>
              <option value="CHAT">Chat</option>
              <option value="TASK">Task</option>
              <option value="EMAIL">Email</option>
            </select>
          </label>
          <label className="text-sm">
            <span className="block text-xs text-neutral-500">Ended</span>
            <select className={inputClass} value={outcome} onChange={(e) => setOutcome(e.target.value)} aria-label="Ended">
              <option value="">Any way</option>
              {Object.entries(OUTCOME_LABEL).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </label>
          <label className="text-sm">
            <span className="block text-xs text-neutral-500">Traffic</span>
            <select className={inputClass} value={traffic} onChange={(e) => setTraffic(e.target.value as typeof traffic)} aria-label="Traffic">
              <option value="real">Real contacts</option>
              <option value="test">Test contacts only</option>
              <option value="all">Real and test</option>
            </select>
          </label>
          {q.data && <p className="ml-auto text-sm text-neutral-600 dark:text-neutral-400">{q.data.contacts.length}{q.data.cursor ? "+" : ""} contacts · {fmt.date(q.data.from)} to {fmt.date(q.data.to)}</p>}
        </div>
        {q.data?.testContacts !== undefined && traffic === "real" && (
          <div className="mb-3">
            <TestTrafficFigure count={q.data.testContacts} viewing={false} onToggle={() => setTraffic("test")} />
          </div>
        )}
        {q.isPending && <LoadingState label="Loading contacts" />}
        {q.isError && <ErrorState error={q.error} onRetry={() => void q.refetch()} />}
        {/* #348: nothing here and nothing ever - say which precondition is missing; a contact before means the filters are why. */}
        {q.data && q.data.contacts.length === 0 && !contactsFilterOnly(q.data) && <SetupChecklist title="No contact records yet" checks={contactsChecks(detail, q.data)} />}
        {q.data && (q.data.contacts.length > 0 || contactsFilterOnly(q.data)) && (
          <DataTable
            caption="Contacts, newest first"
            rows={q.data.contacts}
            rowKey={(c) => c.contactId}
            empty={<EmptyState title="No contacts match" hint={`Widen the dates or clear a filter. The newest contact began ${q.data.lastContactAt ? fmt.when(q.data.lastContactAt) : "outside this range"}.`} />}
            columns={[
              { key: "when", header: "Started", render: (c) => <span title={c.initiationTimestamp}>{fmt.when(c.initiationTimestamp)}</span> },
              { key: "id", header: "Contact", render: (c) => (canInspect ? <Link to={`/journeys/${environmentId}/contacts/${c.contactId}`} className="font-mono text-xs text-accent-strong underline">{c.contactId}</Link> : <span className="font-mono text-xs">{c.contactId}</span>) },
              { key: "channel", header: "Channel", render: (c) => `${c.channel.toLowerCase()} · ${c.initiationMethod.toLowerCase()}` },
              { key: "outcome", header: "Ended", render: (c) => <StatusPill status={OUTCOME_LABEL[c.outcome]} tone={OUTCOME_TONE[c.outcome]} /> },
              { key: "queue", header: "Queue", render: (c) => (c.queueName ? <>{c.queueName}{c.queueSeconds ? <span className="text-xs text-neutral-500"> · {c.queueSeconds}s</span> : null}</> : "-") },
              { key: "handle", header: "Handled", numeric: true, render: (c) => (c.handleSeconds ? `${Math.round(c.handleSeconds / 60)} min` : "-") },
              { key: "dialled", header: "Dialed", render: (c) => c.dialled ?? "-" },
              { key: "flags", header: "", render: (c) => (c.test || c.poorAudio ? <span className="flex gap-1">{c.test && <TestPill />}{c.poorAudio && <StatusPill status="poor audio" tone="warning" />}</span> : null) },
              { key: "ended", header: "Ended at", render: (c) => (c.disconnectTimestamp ? <RelativeTime iso={c.disconnectTimestamp} /> : "-") },
            ]}
          />
        )}
      </Card>
      {canInspect && <CallLookup environmentId={environmentId} />}
    </div>
  );
}
